/**
 * articleService — full text for a single news article.
 * --------------------------------------------------------------------------
 * Three tiers, cheapest first:
 *
 *   1. FEED       The item's own <content:encoded>. Free, instant, offline.
 *                 Only The Race ships the whole article this way; the other
 *                 five send a 100–400 character teaser, which fails the word
 *                 threshold and falls through.
 *
 *   2. PROXY      GET /api/article?url=… → the publisher's page → extractor.
 *                 Available wherever the dev server (or an equivalent edge
 *                 function) is serving /api/*. See scripts/vite-article-proxy.
 *
 *   3. SUMMARY    The feed teaser we already show on the card, plus a link
 *                 out. Always available, so the reader never dead-ends.
 *
 * Tier 3 is a legitimate outcome, not an error: publishers rework their markup,
 * some pages are paywalled, and a packaged build has no proxy at all. The
 * reader states plainly which tier it is showing rather than implying the
 * teaser is the whole article.
 */

import { extractArticle, blocksFromHtmlFragment, readingMinutes } from '../utils/articleExtractor.js';
import { isAllowedArticleUrl } from '../data/newsSources.js';
import { apiUrl } from './apiBase.js';

const CACHE = new Map();
const CACHE_TTL_MS = 30 * 60 * 1000;
const MAX_CACHED = 40;
const PROXY_TIMEOUT_MS = 15_000;

export const CONTENT_TIERS = { FEED: 'feed', PROXY: 'proxy', SUMMARY: 'summary' };

function readCache(key) {
  const entry = CACHE.get(key);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    CACHE.delete(key);
    return null;
  }
  return entry.value;
}

function writeCache(key, value) {
  CACHE.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
  // Cheap FIFO trim — insertion order is Map's iteration order.
  while (CACHE.size > MAX_CACHED) CACHE.delete(CACHE.keys().next().value);
}

export function clearArticleCache() {
  CACHE.clear();
}

/**
 * summaryFallback — tier 3. One paragraph, built from what the card already
 * had, so this can never itself fail.
 */
function summaryFallback(article) {
  const text = (article.summary ?? '').trim();
  return {
    tier: CONTENT_TIERS.SUMMARY,
    blocks: text ? [{ type: 'paragraph', text }] : [],
    wordCount: text.split(/\s+/).filter(Boolean).length,
    readTime: article.readTime ?? 1,
    complete: false,
  };
}

/**
 * fetchArticleContent — resolve the best available body for an article.
 *
 * Never rejects for content reasons; a failed fetch degrades to the summary
 * tier. It still propagates AbortError so the reader can close mid-flight
 * without a stray state update.
 *
 * @param {object} article  a normalised NewsArticle (see utils/newsParser)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.forceRefresh]
 * @returns {Promise<{tier, blocks, wordCount, readTime, complete, error?}>}
 */
export async function fetchArticleContent(article, { signal, forceRefresh = false } = {}) {
  if (!article) return summaryFallback({});

  const key = article.originalUrl || article.id;
  if (!forceRefresh) {
    const cached = readCache(key);
    if (cached) return cached;
  }

  // ---- Tier 1: the feed already has the whole thing -----------------------
  if (article.contentHtml) {
    const fromFeed = blocksFromHtmlFragment(article.contentHtml, {
      baseUrl: article.originalUrl,
      title: article.title,
    });
    if (fromFeed.ok) {
      const value = {
        tier: CONTENT_TIERS.FEED,
        blocks: fromFeed.blocks,
        wordCount: fromFeed.wordCount,
        readTime: readingMinutes(fromFeed.wordCount),
        complete: true,
      };
      writeCache(key, value);
      return value;
    }
  }

  // ---- Tier 2: fetch and extract the publisher's page ---------------------
  if (isAllowedArticleUrl(article.originalUrl)) {
    try {
      const html = await fetchThroughProxy(article.originalUrl, signal);
      const extracted = extractArticle(html, {
        baseUrl: article.originalUrl,
        title: article.title,
      });
      if (extracted.ok) {
        const value = {
          tier: CONTENT_TIERS.PROXY,
          blocks: extracted.blocks,
          wordCount: extracted.wordCount,
          readTime: readingMinutes(extracted.wordCount),
          complete: true,
        };
        writeCache(key, value);
        return value;
      }
      // Parsed fine, just nothing readable in it — a paywall or consent wall.
      const value = { ...summaryFallback(article), error: 'no-readable-content' };
      writeCache(key, value);
      return value;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      // Not cached: a proxy that is merely absent (packaged build) or briefly
      // unreachable should be retried the next time the article is opened.
      return { ...summaryFallback(article), error: err.message };
    }
  }

  return { ...summaryFallback(article), error: 'source-not-allowlisted' };
}

async function fetchThroughProxy(url, signal) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROXY_TIMEOUT_MS);
  const forward = () => controller.abort();
  signal?.addEventListener('abort', forward, { once: true });

  try {
    const response = await fetch(apiUrl(`/api/article?url=${encodeURIComponent(url)}`), {
      signal: controller.signal,
      headers: { Accept: 'text/plain' },
    });

    if (!response.ok) {
      throw new Error(`article proxy ${response.status}`);
    }

    // A packaged build has no /api/* route, so index.html comes back with a
    // 200. Detect that rather than handing the extractor the app shell.
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) {
      throw new Error('article proxy unavailable');
    }

    return await response.text();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forward);
  }
}
